import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Send, Users, Mail, AlertCircle, CheckCircle, Info, XCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface User {
  id: string;
  email: string;
  profile?: {
    organization_name: string;
    industry: string;
    country: string;
  };
}

interface AdminNotificationSenderProps {
  users: User[];
}

const AdminNotificationSender: React.FC<AdminNotificationSenderProps> = ({ users }) => {
  const [activeTab, setActiveTab] = useState('single');
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedUserEmail, setSelectedUserEmail] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'info' | 'success' | 'warning' | 'error' | 'analysis_complete' | 'backlink_placed' | 'content_generated'>('info');
  const [actionUrl, setActionUrl] = useState('');
  const [actionText, setActionText] = useState('');
  const [bulkEmails, setBulkEmails] = useState<string>('');
  
  const { toast } = useToast();

  const notificationTypeOptions = [
    { value: 'info', label: 'Info', icon: Info, color: 'bg-blue-100 text-blue-800' },
    { value: 'success', label: 'Success', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
    { value: 'warning', label: 'Warning', icon: AlertCircle, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'error', label: 'Error', icon: XCircle, color: 'bg-red-100 text-red-800' },
    { value: 'analysis_complete', label: 'Analysis Complete', icon: CheckCircle, color: 'bg-purple-100 text-purple-800' },
    { value: 'backlink_placed', label: 'Backlink Placed', icon: CheckCircle, color: 'bg-indigo-100 text-indigo-800' },
    { value: 'content_generated', label: 'Content Generated', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-800' },
  ];

  const selectedType = notificationTypeOptions.find(option => option.value === notificationType);
  const TypeIcon = selectedType?.icon || Info;

  const sendSingleNotification = async () => {
    if (!selectedUser && !selectedUserEmail) {
      toast({
        title: "Error",
        description: "Please select a user or enter an email address.",
        variant: "destructive",
      });
      return;
    }

    if (!title.trim() || !message.trim()) {
      toast({
        title: "Error",
        description: "Please fill in both title and message.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-send-notification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'send_notification',
          userId: selectedUser || undefined,
          userEmail: selectedUserEmail || undefined,
          title: title.trim(),
          message: message.trim(),
          notificationType,
          actionUrl: actionUrl.trim() || undefined,
          actionText: actionText.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send notification');
      }

      toast({
        title: "Success",
        description: "Notification sent successfully!",
      });

      // Reset form
      setSelectedUser('');
      setSelectedUserEmail('');
      setTitle('');
      setMessage('');
      setActionUrl('');
      setActionText('');
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Failed to send notification',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const sendBulkNotification = async () => {
    if (!selectedUsers.length && !bulkEmails.trim()) {
      toast({
        title: "Error",
        description: "Please select users or enter email addresses.",
        variant: "destructive",
      });
      return;
    }

    if (!title.trim() || !message.trim()) {
      toast({
        title: "Error",
        description: "Please fill in both title and message.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      const userEmails = bulkEmails.trim() ? bulkEmails.split(',').map(email => email.trim()).filter(Boolean) : undefined;
      const userIds = selectedUsers.length > 0 ? selectedUsers : undefined;

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/admin-send-notification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'send_bulk_notification',
          userEmails,
          userIds,
          title: title.trim(),
          message: message.trim(),
          notificationType,
          actionUrl: actionUrl.trim() || undefined,
          actionText: actionText.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send bulk notification');
      }

      toast({
        title: "Success",
        description: result.message || 'Bulk notification sent successfully!',
      });

      // Reset form
      setSelectedUsers([]);
      setBulkEmails('');
      setTitle('');
      setMessage('');
      setActionUrl('');
      setActionText('');
    } catch (error) {
      console.error('Error sending bulk notification:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Failed to send bulk notification',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSend = () => {
    if (activeTab === 'single') {
      sendSingleNotification();
    } else {
      sendBulkNotification();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Send Notifications
        </CardTitle>
        <CardDescription>
          Send notifications to individual users or multiple users at once
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single">Single User</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Send</TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select User</label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex flex-col">
                          <span>{user.email}</span>
                          {user.profile?.organization_name && (
                            <span className="text-xs text-muted-foreground">
                              {user.profile.organization_name}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Or Enter Email</label>
                <Input
                  placeholder="user@example.com"
                  value={selectedUserEmail}
                  onChange={(e) => setSelectedUserEmail(e.target.value)}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bulk" className="space-y-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select Multiple Users</label>
                <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-2">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                        selectedUsers.includes(user.id)
                          ? 'bg-primary/10 border border-primary/20'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => {}}
                        className="rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{user.email}</div>
                        {user.profile?.organization_name && (
                          <div className="text-xs text-muted-foreground truncate">
                            {user.profile.organization_name}
                          </div>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {selectedUsers.includes(user.id) ? 'Selected' : 'Click to select'}
                      </Badge>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Selected: {selectedUsers.length} users
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Or Enter Email Addresses</label>
                <Textarea
                  placeholder="user1@example.com, user2@example.com, user3@example.com"
                  value={bulkEmails}
                  onChange={(e) => setBulkEmails(e.target.value)}
                  className="min-h-[80px]"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  Separate multiple emails with commas
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Common notification form */}
          <div className="space-y-4 pt-4 border-t">
            <div>
              <label className="text-sm font-medium mb-2 block">Notification Type</label>
              <Select value={notificationType} onValueChange={(value: any) => setNotificationType(value)}>
                <SelectTrigger>
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <TypeIcon className="h-4 w-4" />
                      {selectedType?.label}
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {notificationTypeOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {option.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Title *</label>
              <Input
                placeholder="Notification title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Message *</label>
              <Textarea
                placeholder="Notification message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[100px]"
                maxLength={500}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Action URL (Optional)</label>
                <Input
                  placeholder="https://example.com"
                  value={actionUrl}
                  onChange={(e) => setActionUrl(e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Action Text (Optional)</label>
                <Input
                  placeholder="View Details"
                  value={actionText}
                  onChange={(e) => setActionText(e.target.value)}
                />
              </div>
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {activeTab === 'single' 
                  ? 'This notification will be sent to the selected user immediately.'
                  : `This notification will be sent to ${selectedUsers.length + (bulkEmails.trim() ? bulkEmails.split(',').length : 0)} users immediately.`
                }
              </AlertDescription>
            </Alert>

            <Button 
              onClick={handleSend} 
              disabled={loading || (!title.trim() || !message.trim())}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Notification{activeTab === 'bulk' ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AdminNotificationSender;
